import * as React from 'react';
import { SegmentedControl, Sheet, SheetStat } from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { cn } from '@/lib/utils/cn';
import {
  useModuleContentQuery,
  useModuleVersionsQuery,
  useVersionDiffQuery,
  type ModuleContentItem,
} from '@/lib/api/requirement-library';
import { formatDate, plural } from './library-logic';

/**
 * Version history of one module (T-0141, E11-F01 scenario 3 and E11-F02
 * scenario 5): open any earlier version exactly as it was published, or
 * compare two versions to see what was added, reworded and retired.
 *
 * Published versions are immutable, so everything here is read-only for
 * every firm user.
 */

export type HistoryTarget = { moduleId: string; view: number | 'compare' };

export interface VersionHistorySheetProps {
  target: HistoryTarget | null;
  moduleName: string;
  onClose: () => void;
}

type Mode = 'view' | 'compare';

const SELECT =
  'h-8 rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

export function VersionHistorySheet({ target, moduleName, onClose }: VersionHistorySheetProps) {
  const moduleId = target?.moduleId ?? null;
  const versionsQuery = useModuleVersionsQuery(moduleId);
  const versions = React.useMemo(() => versionsQuery.data?.versions ?? [], [versionsQuery.data]);
  const latest = versions[0]?.version ?? null;

  const [mode, setMode] = React.useState<Mode>('view');
  const [viewVersion, setViewVersion] = React.useState<number | null>(null);
  const [from, setFrom] = React.useState<number | null>(null);
  const [to, setTo] = React.useState<number | null>(null);

  // Take the requested view each time the sheet is opened.
  React.useEffect(() => {
    if (!target) return;
    if (target.view === 'compare') {
      setMode('compare');
      setViewVersion(null);
    } else {
      setMode('view');
      setViewVersion(target.view);
    }
    setFrom(null);
    setTo(null);
  }, [target]);

  const shownVersion = viewVersion ?? latest;
  const toVersion = to ?? latest;
  const fromVersion = from ?? (toVersion != null && toVersion > 1 ? toVersion - 1 : null);

  const contentQuery = useModuleContentQuery(
    mode === 'view' ? moduleId : null,
    mode === 'view' ? shownVersion : null,
  );
  const diffQuery = useVersionDiffQuery(
    mode === 'compare' ? moduleId : null,
    fromVersion,
    toVersion,
  );
  const canCompare = versions.length > 1;

  return (
    <Sheet
      open={target != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`${moduleName} — version history`}
      description="Published versions are kept exactly as they were published."
      headerActions={
        canCompare ? (
          <SegmentedControl<Mode>
            aria-label="History view"
            value={mode}
            onChange={setMode}
            options={[
              { value: 'view', label: 'Read a version' },
              { value: 'compare', label: 'Compare two versions' },
            ]}
          />
        ) : null
      }
      summary={
        mode === 'compare' && diffQuery.data ? (
          <>
            <SheetStat value={diffQuery.data.counts.added} label="added" />
            <SheetStat value={diffQuery.data.counts.reworded} label="reworded" />
            <SheetStat value={diffQuery.data.counts.retired} label="retired" />
          </>
        ) : mode === 'view' && contentQuery.data ? (
          <SheetStat value={contentQuery.data.items.length} label="requirements in this version" />
        ) : undefined
      }
    >
      <div className="w-[300px] flex-none overflow-y-auto border-r border-ink-100 bg-paper-50 p-2">
        {versionsQuery.isPending ? (
          <div className="p-3">
            <Spinner />
          </div>
        ) : versionsQuery.isError ? (
          <ErrorMessage error={versionsQuery.error} />
        ) : (
          <ul aria-label="Versions" className="flex flex-col gap-1">
            {versions.map((v) => {
              const active = mode === 'view' && v.version === shownVersion;
              return (
                <li key={v.version}>
                  <button
                    type="button"
                    aria-current={active ? 'true' : undefined}
                    onClick={() => {
                      setMode('view');
                      setViewVersion(v.version);
                    }}
                    className={cn(
                      'w-full rounded-chip border px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                      active
                        ? 'border-ink-100 bg-paper-0 shadow-[inset_2px_0_0_var(--accent-amber-600)]'
                        : 'border-transparent hover:bg-paper-100',
                    )}
                  >
                    <div className="flex items-baseline gap-2 text-body-sm font-semibold text-ink-900">
                      <span className="font-mono">v{v.version}</span>
                      {v.is_current ? (
                        <span className="text-meta font-medium text-ink-500">Current</span>
                      ) : null}
                      <span className="ml-auto font-mono text-meta font-normal text-ink-500">
                        {v.item_count}
                      </span>
                    </div>
                    <div className="text-meta text-ink-500">
                      {formatDate(v.published_at)}
                      {v.published_by.name ? ` · ${v.published_by.name}` : ''}
                    </div>
                    <div className="mt-0.5 text-meta text-ink-700">
                      {v.change_note || 'No change note'}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="min-w-0 flex-1 overflow-y-auto p-5">
        {mode === 'view' ? (
          contentQuery.isPending ? (
            <Spinner />
          ) : contentQuery.isError ? (
            <ErrorMessage error={contentQuery.error} />
          ) : (
            <VersionContent
              heading={`Version ${contentQuery.data.version?.version ?? shownVersion ?? ''}${
                contentQuery.data.version?.is_current ? ' (current)' : ''
              }`}
              items={contentQuery.data.items}
            />
          )
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-body-sm text-ink-700">
              <label htmlFor="rl-diff-from">From</label>
              <select
                id="rl-diff-from"
                className={SELECT}
                value={fromVersion ?? ''}
                onChange={(e) => setFrom(Number(e.target.value))}
              >
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version}
                  </option>
                ))}
              </select>
              <label htmlFor="rl-diff-to">to</label>
              <select
                id="rl-diff-to"
                className={SELECT}
                value={toVersion ?? ''}
                onChange={(e) => setTo(Number(e.target.value))}
              >
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version}
                  </option>
                ))}
              </select>
            </div>
            {fromVersion == null || toVersion == null || fromVersion === toVersion ? (
              <p className="text-body-sm text-ink-500">Choose two different versions to compare.</p>
            ) : diffQuery.isPending ? (
              <Spinner />
            ) : diffQuery.isError ? (
              <ErrorMessage error={diffQuery.error} />
            ) : (
              <div className="flex flex-col gap-5">
                {diffQuery.data.renamed ? (
                  <p
                    className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700"
                    aria-label="Module renamed"
                  >
                    <span className="font-semibold text-ink-900">Renamed.</span> The module was
                    &ldquo;{diffQuery.data.renamed.from}&rdquo; in v{diffQuery.data.from} and is
                    &ldquo;{diffQuery.data.renamed.to}&rdquo; in v{diffQuery.data.to}. Its prefix
                    and numbers did not change.
                  </p>
                ) : null}
                {diffQuery.data.counts.added +
                  diffQuery.data.counts.reworded +
                  diffQuery.data.counts.retired ===
                  0 && !diffQuery.data.renamed ? (
                  <p className="text-body-sm text-ink-500">
                    Nothing differs between v{diffQuery.data.from} and v{diffQuery.data.to}.
                  </p>
                ) : null}
                <DiffSection title="Added" count={diffQuery.data.added.length}>
                  {diffQuery.data.added.map((a) => (
                    <DiffRow key={a.item_id} number={a.number} sub={a.subcategory}>
                      {a.text}
                    </DiffRow>
                  ))}
                </DiffSection>
                <DiffSection title="Reworded" count={diffQuery.data.reworded.length}>
                  {diffQuery.data.reworded.map((r) => (
                    <DiffRow key={r.item_id} number={r.number} sub={r.to.subcategory}>
                      <span className="text-ink-500 line-through">{r.from.text}</span>
                      <br />
                      {r.to.text}
                      {r.from.subcategory !== r.to.subcategory ? (
                        <span className="block text-meta text-ink-500">
                          Moved from {r.from.subcategory} to {r.to.subcategory}
                        </span>
                      ) : null}
                    </DiffRow>
                  ))}
                </DiffSection>
                <DiffSection title="Retired" count={diffQuery.data.retired.length}>
                  {diffQuery.data.retired.map((a) => (
                    <DiffRow key={a.item_id} number={a.number} sub={a.subcategory}>
                      <span className="text-ink-500 line-through">{a.text}</span>
                    </DiffRow>
                  ))}
                </DiffSection>
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

function VersionContent({
  heading,
  items,
}: {
  heading: string;
  items: ReadonlyArray<ModuleContentItem>;
}) {
  const groups = React.useMemo(() => {
    const map = new Map<string, ModuleContentItem[]>();
    for (const item of items) {
      const bucket = map.get(item.subcategory);
      if (bucket) bucket.push(item);
      else map.set(item.subcategory, [item]);
    }
    return Array.from(map.entries());
  }, [items]);
  return (
    <div>
      <h3 className="text-h3 font-semibold text-ink-900">{heading}</h3>
      <p className="mb-3 text-meta text-ink-500">{plural(items.length, 'requirement')}</p>
      {groups.map(([sub, rows]) => (
        <section key={sub} aria-label={sub} className="mb-4">
          <h4 className="border-b border-ink-100 bg-paper-50 px-3 py-1.5 text-meta font-semibold text-ink-700">
            {sub} <span className="ml-1 font-mono font-normal text-ink-500">{rows.length}</span>
          </h4>
          <ul>
            {rows.map((r) => (
              <li
                key={r.item_id}
                className="grid grid-cols-[104px_1fr] gap-3 border-b border-ink-100 px-3 py-2 text-body-sm"
              >
                <span className="font-mono text-meta font-medium text-ink-700">{r.number}</span>
                <span className="text-ink-900">{r.text}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function DiffSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  if (count === 0) return null;
  return (
    <section aria-label={title}>
      <h3 className="mb-1.5 text-meta font-medium uppercase tracking-wide text-ink-500">
        {title} <span className="font-mono normal-case tracking-normal">{count}</span>
      </h3>
      <ul className="rounded-card border border-ink-100">{children}</ul>
    </section>
  );
}

function DiffRow({
  number,
  sub,
  children,
}: {
  number: string;
  sub: string;
  children: React.ReactNode;
}) {
  return (
    <li className="grid grid-cols-[104px_1fr] gap-3 border-b border-ink-100 px-3 py-2 text-body-sm last:border-b-0">
      <span>
        <span className="block font-mono text-meta font-medium text-ink-700">{number}</span>
        <span className="block text-meta text-ink-500">{sub}</span>
      </span>
      <span className="min-w-0 text-ink-900">{children}</span>
    </li>
  );
}
